import { Button, Form, Input, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { SiMicrosoftexcel } from "react-icons/si";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import Loading from "../../components/loading";
import API from "../../config/api";
import { DELETE, GET, POST } from "../../utils/apiCalls";
import PageHeader from "../routes/pageHeader";
import DataTable from "./components/dataTable";
import Forms from "./components/form";
import "./styles.scss";

function UserScreen() {
  const navigate = useNavigate();
  const Events = useSelector((state: any) => state.Events);
  const User = useSelector((state: any) => state.User);
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState({
    list: true,
    excel: false,
  });
  const [data, setData] = useState([]);
  const [selectedItem, setSelectedItem] = useState<any>({});
  const [openForm, setOpenForm] = useState(false);
  const [meta, setMeta] = useState<any>({});
  const [data1, setData1] = useState(1);

  useEffect(() => {
    fetchData();
  }, [data1]);

  const fetchData = useCallback(async () => {
    setIsLoading((prev) => ({ ...prev, list: true }));
    try {
      const params = {
        ...(searchParams.get("query") && { query: searchParams.get("query") }),
        ...(searchParams.get("page") && { page: searchParams.get("page") }),
        ...(searchParams.get("limit") && { take: searchParams.get("limit") }),
        ...(searchParams.get("sortField") && { sortField: searchParams.get("sortField") }),
        ...(searchParams.get("sortOrder") && { sortOrder: searchParams.get("sortOrder") }),
        eventId: User?.user?.companyId,
      };
      const res: any = await GET(API.USERS_LIST_SEARCH, params);
      if (res.status) {
        setData(res.data);
        setMeta(res.meta);
      } else {
        message.error("Failed to fetch user data.");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while fetching data.");
    } finally {
      setIsLoading((prev) => ({ ...prev, list: false }));
      setLoading(false);
    }
  }, [User?.user?.companyId, searchParams]);

  const generateExcel = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, excel: true }));
      
      // Use same API with current filters and sorting, but without pagination
      const params = {
        ...(searchParams.get("query") && { query: searchParams.get("query") }),
        ...(searchParams.get("sortField") && { sortField: searchParams.get("sortField") }),
        ...(searchParams.get("sortOrder") && { sortOrder: searchParams.get("sortOrder") }),
        eventId: User?.user?.companyId,
        // Don't include page and take for Excel export
      };
      
      const response: any = await GET(API.USERS_LIST_SEARCH, params);
      
      if (response?.status) {
        // Format the data with only required fields
        const formattedData = response.data.map((user: any, index: number) => ({
          No: index + 1,
          Name: user.name || "",
          Email: user.email || "",
          Phone: user.phoneNumber || "",
          Role: user.role || "",
          UserType: user.userType || "",
          Status: user.status ? "Active" : "Inactive",
          "Created At": user.createdAt ? new Date(user.createdAt).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }) : "",
          "Updated At": user.updatedAt ? new Date(user.updatedAt).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }) : "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        
        // Set column widths for better readability
        const colWidths = [
          { wch: 5 },  // No
          { wch: 20 }, // Name
          { wch: 25 }, // Email
          { wch: 15 }, // Phone
          { wch: 15 }, // Role
          { wch: 12 }, // UserType
          { wch: 10 }, // Status
          { wch: 20 }  // CreatedAt
        ];
        worksheet["!cols"] = colWidths;
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
        
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        
        const dataBlob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });
        
        const link = document.createElement("a");
        link.href = URL.createObjectURL(dataBlob);
        link.download = `Users-Export-${new Date().toLocaleString()}.xlsx`;
        link.click();
        
        message.success("Excel export completed successfully!");
      } else {
        message.error("Couldn't generate reports");
      }
    } catch (err) {
      console.error(err);
      message.error("Couldn't generate reports");
    } finally {
      setIsLoading((prev) => ({ ...prev, excel: false }));
    }
  };

  const editItem = (value: any) => {
    setSelectedItem(value);
    setOpenForm(true);
  };

  // Updated changeParams function similar to other modules
  const changeParams = (data: any) => {
    // Preserve sorting parameters if not explicitly provided
    const sortField = data?.sortField !== undefined ? data.sortField : searchParams.get("sortField");
    const sortOrder = data?.sortOrder !== undefined ? data.sortOrder : searchParams.get("sortOrder");
    
    const queryParams = [
      { key: "query", value: data?.query !== undefined ? data.query : (data?.val && searchParams.get("query")) },
      { key: "sortField", value: sortField },
      { key: "sortOrder", value: sortOrder },
    ]
      .filter((param: any) => param?.value !== null && param?.value !== undefined && param?.value !== "" && param?.value !== "null")
      .map((param: any) => `&${param.key}=${param?.value}`)
      .join("");
      
    const baseUrl = `/company/users?page=${
      data?.page || searchParams.get("page") || 1
    }&limit=${data?.pageSize || searchParams.get("limit") || 10}`;
    
    navigate(`${baseUrl}${queryParams}`);
    setData1((prev) => prev + 1);
  };

  const onSortChange = (sortField: string | undefined, sortOrder: string | undefined) => {
    changeParams({
      sortField,
      sortOrder,
      page: 1, // Reset to first page when sorting
    });
  };

  // Updated form change handler to preserve sorting
  const onFormChange = (_:any, allData: any) => {
    changeParams({
      ...allData,
      page: 1,
      // Preserve existing sorting by explicitly passing current URL params
      sortField: searchParams.get("sortField"),
      sortOrder: searchParams.get("sortOrder"),
    });
  };

  const toggleStatus = useCallback(
    async (value: any) => {
      try {
        setIsLoading((prev) => ({ ...prev, list: true }));
        const obj = { ...value, password: null, status: !value.status };
        const response: any = await POST(API.USERS_EDIT, obj);
        if (response.status) {
          fetchData();
          message.success("Successfully updated");
        } else {
          message.error("Something went wrong.");
        }
      } catch (err) {
        message.error("Something went wrong.");
      } finally {
        setIsLoading((prev) => ({ ...prev, list: false }));
      }
    },
    [fetchData]
  );

  const deleteItem = useCallback(
    async (id: string) => {
      try {
        setIsLoading((prev) => ({ ...prev, list: true }));
        const response: any = await DELETE(`${API.USERS_DELETE}${id}`);
        if (response?.status) {
          fetchData();
          message.success("Deleted successfully.");
        } else {
          message.error("Something went wrong.");
        }
      } catch (err) {
        message.error("Something went wrong.");
      } finally {
        setIsLoading((prev) => ({ ...prev, list: false }));
      }
    },
    [fetchData]
  );

  return (
    <div>
      <PageHeader title="Users">
        <Form
          form={form}
          onValuesChange={onFormChange}
          initialValues={{ query: searchParams.get("query") }}
        >
          <div className="AdminHeader-extra">
            <Form.Item name="query" noStyle>
              <Input style={{ width: 200 }} placeholder="Search" allowClear />
            </Form.Item>
            <Form.Item noStyle>
              {Array.isArray(User?.user.access[Events?.defult?.id]) &&
               User?.user.access[Events?.defult?.id].includes(8) ? null : (
                <>
                  <Button type="primary" onClick={() => setOpenForm(true)}>
                    ADD USER +
                  </Button>
                  <Button
                    loading={isLoading.excel}
                    onClick={generateExcel}
                    style={{ padding: 5, background: "#fff", cursor: "pointer", marginLeft: 8 }}
                  >
                    EXPORT <SiMicrosoftexcel size={20} color="green" />
                  </Button>
                </>
               )}
            </Form.Item>
          </div>
        </Form>
      </PageHeader>

      {loading ? (
        <Loading />
      ) : (
        <DataTable
          data={data}
          meta={meta}
          loading={isLoading.list}
          toggleStatus={toggleStatus}
          onChange={editItem}
          delete={deleteItem}
          onSortChange={onSortChange}
          onPageChange={(page: number, pageSize: number) =>
            changeParams({ page, pageSize, val: true })
          }
        />
      )}

      {openForm && (
        <Forms
          item={Events.events}
          visible={openForm}
          data={selectedItem}
          companyId={User?.user?.companyId}
          onClose={() => {
            setSelectedItem({});
            setOpenForm(!openForm);
          }}
          onChange={fetchData}
        />
      )}
      <br />
    </div>
  );
}

export default UserScreen;